// The send queue: KIT, F26 links, outreach-ui and the keyboard flow, on one page. It never sends. A person copies, sends by
// hand, then marks it, which writes the touch and moves the stage on. A marked row stays where it is, so the cursor can't
// slide onto the next company.
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../../../lib'
import { usePaletteActions } from '../app/Palette'
import { Busy, Button, Err, Page, Strip, Table, copyText, errorText, since, useCopy, useCursor, useKeys, useKit, type Column } from '../kit'
import { fillMessage, linkedinHref, nameOf, queryOf, rememberList, routeWord, safeHref, sourceTag, useFilters, useMeta, withParam, type Imported, type Sync, type Track } from '../records/lib'
import { Check, FilterBar, ReadLine, SearchField, Select, TooMany, lastTouch, pageCheck } from '../records/parts'

type Row = {
  id: string; list: string; channel: string; message: string | null; variants: string[]; old_wording: boolean; link: string | null; state: 'queued' | 'sent' | 'skipped'; done_at: string | null; done_by: string | null
  override_reason: string | null; undo_until: string | null; relationship_id: string; track: Track; stage: string; stage_label: string; owner: string | null
  company_id: string; company: string; person_id: string | null; person: string | null; person_title: string | null; linkedin_url: string | null
  last_at: string | null; last_actor: string | null; last_summary: string | null; page_slug: string | null; page_state: string | null; page_checked_at: string | null; page_check_status: number | null; messaged_at: string | null; messaged_by: string | null; already?: string; blocked: string | null; refused: boolean; warm: string[]; route: string | null; note: string | null; greeting_note: string | null
}
type Data = {
  at: string; rows: Row[]; groups: { key: string; label: string; n: number }[]; lists: { list: string; queued: number; total: number }[]
  hidden_yc: number; truncated: boolean; undo_minutes: number; sync: Sync; pages_checked_at: string | null; imported: Imported
}

const SOURCES = [{ value: 'auto', label: 'Tag by channel' }, { value: 'none', label: 'No source tag' }, { value: 'dm', label: 'LinkedIn DM' }, { value: 'bookface', label: 'Bookface' }, { value: 'chat', label: 'Chat' }]
const SRC_KEY = 'st-send-source'
const readSrc = () => { try { return localStorage.getItem(SRC_KEY) ?? 'auto' } catch { return 'auto' } }
const keepSrc = (v: string) => { try { localStorage.setItem(SRC_KEY, v) } catch { /* the filter still works for this page */ } }
const SERVER_FILTERS = ['q', 'list', 'owner', 'mine', 'state', 'internal']

export function SendPage() {
  const { sp, get, put } = useFilters()
  const navigate = useNavigate()
  const { toast, confirm } = useKit()
  const qc = useQueryClient()
  const meta = useMeta()
  const me = meta.data?.me ?? null
  const [src, setSrc] = useState(readSrc)
  const [kept, setKept] = useState<string[]>([])     // rows marked on this page stay on it until it reloads
  const busy = useRef(new Set<string>())
  const group = get('group') || 'ready'
  const state = get('state') || 'queued'

  const search = queryOf(sp, SERVER_FILTERS, { group, ...(kept.length ? { keep: kept.join(',') } : {}) })
  const q = useQuery({
    queryKey: ['crm', 'send', search],
    queryFn: () => api<Data>('GET', `/api/staff/crm/send${search}`),
    placeholderData: (p) => p,
    refetchInterval: 30_000,
  })
  const d = q.data
  const rows = d?.rows ?? []
  useEffect(() => { if (rows.length) rememberList('company', [...new Set(rows.map((r) => r.company_id))]) }, [rows])
  const { cur, row, setCur } = useCursor(rows, (r) => r.id, (r) => navigate(`/staff/companies/${r.company_id}`))

  // On a wide screen the first row is ready to go. On a phone a tap opens the sheet.
  const first = rows[0]?.id
  useEffect(() => {
    if (first && !cur && window.matchMedia('(min-width: 1101px)').matches) setCur(first)
  }, [first])

  const srcFor = (r: Row): string | null => sourceTag(src, r.channel)
  const link = (r: Row): string | null => {
    const pattern = get('pattern')
    const base = pattern && r.page_slug ? pattern.replaceAll('{slug}', r.page_slug) : r.link
    if (!base) return null
    const tag = srcFor(r)
    return tag ? withParam(base, 'src', tag) : base
  }
  const message = (r: Row): string | null => (r.message ? fillMessage(r.message, { link: link(r), person: r.person, company: r.company, strip: get('nopage') === '1' }) : null)
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['crm'] })
    void qc.invalidateQueries({ queryKey: ['staff', 'counts'] })
  }

  /** One write per row at a time. Two quick presses can't mark a row twice. */
  const lock = async (id: string, fn: () => Promise<void>) => {
    if (busy.current.has(id)) return
    busy.current.add(id)
    try {
      await fn()
    } catch (e) {
      toast(errorText(e), { bad: true })
    } finally {
      busy.current.delete(id)
    }
  }
  const undo = (r: Row) => lock(r.id, async () => {
    await api('POST', `/api/staff/crm/send/${r.id}/undo`, {})
    toast(`Undone: ${r.company}`)
    refresh()
  })
  const done = (r: Row, verb: string, path: string, body: unknown) => lock(r.id, async () => {
    await api('POST', `/api/staff/crm/send/${r.id}/${path}`, body)
    setKept((k) => (k.includes(r.id) ? k : [...k, r.id]))
    toast(`${verb}: ${r.company}`, { action: { label: 'Undo', run: () => void undo(r) } })
    refresh()
  })
  const markSent = async (r: Row | null) => {
    if (!r) return
    if (r.state !== 'queued') { toast('Already marked'); return }
    if (r.refused) { toast("They unsubscribed, so this can't go", { bad: true }); return }
    const body: { src: string | null; override_reason?: string; body?: string } = { src: srcFor(r), body: message(r) ?? undefined }
    if (r.blocked) {     // a hold, or a message already on record: both stop here for a typed reason
      const c = await confirm({
        title: `Mark ${r.company} sent anyway`, body: r.blocked, confirm: 'Mark sent', danger: true,
        reason: { label: 'Why you sent anyway', required: true },
      })
      if (!c.ok) return
      body.override_reason = c.reason
    }
    await done(r, 'Marked sent', 'sent', body)
  }
  const skip = async (r: Row | null) => {
    if (!r) return
    if (r.state !== 'queued') { toast('Already marked'); return }
    await done(r, 'Skipped', 'skip', {})
  }
  const undoRow = async (r: Row | null) => {
    if (!r) return
    if (r.state === 'queued') { toast('Nothing to undo'); return }
    await undo(r)
  }
  const copy = useCopy()
  const copyMessage = async (r: Row | null) => {
    if (!r) return
    const m = message(r)
    if (!m) { toast('No message on this row', { bad: true }); return }
    await copy(m, 'Message copied')
  }
  const copyVariant = async (r: Row | null, n: number) => {
    const text = r?.variants[n]
    if (!r || !text) return
    await copy(fillMessage(text, { link: link(r), person: r.person, company: r.company, strip: get('nopage') === '1' }), `Message ${n + 1} copied`)
  }
  const copyLink = async (r: Row | null) => {
    if (!r) return
    const l = link(r)
    if (!l) { toast('No link on this row', { bad: true }); return }
    await copy(l, 'Link copied')
  }
  const openLinkedIn = async (r: Row | null) => {
    if (!r) return
    // Open first, inside the key press, or Safari blocks it. Then say what really happened.
    const w = window.open('', '_blank')
    if (!w) { toast('The browser blocked the new tab', { bad: true }); return }
    w.opener = null
    w.location.href = linkedinHref(r.linkedin_url, r.person, r.company)
    const text = message(r) ?? link(r)
    const ok = text ? await copyText(text) : false
    toast(!text ? 'Opened LinkedIn' : ok ? `Opened LinkedIn, ${message(r) ? 'message' : 'link'} copied` : 'Opened LinkedIn. Copy failed, press c here', { bad: !!text && !ok })
  }
  const preview = (r: Row | null) => {
    const href = r ? safeHref(r.link) : undefined
    if (href) window.open(withParam(href, 'ernd_internal', '1'), '_blank', 'noopener')
    else if (r) toast('No link on this row', { bad: true })
  }

  useKeys({ 1: () => void copyVariant(row, 0), 2: () => void copyVariant(row, 1), 3: () => void copyVariant(row, 2), 4: () => void copyVariant(row, 3), c: () => void copyMessage(row), l: () => void copyLink(row), o: () => void openLinkedIn(row), p: () => preview(row), d: () => void markSent(row), s: () => void skip(row), u: () => void undoRow(row) })
  usePaletteActions('send', row ? [
    { id: 'copy', label: `Copy message: ${row.company}`, hint: 'c', run: () => void copyMessage(row) },
    { id: 'link', label: `Copy link: ${row.company}`, hint: 'l', run: () => void copyLink(row) },
    { id: 'sent', label: `Mark sent: ${row.company}`, hint: 'd', run: () => void markSent(row) },
    { id: 'skip', label: `Skip: ${row.company}`, hint: 's', run: () => void skip(row) },
    { id: 'undo', label: `Undo: ${row.company}`, hint: 'u', run: () => void undoRow(row) },
  ] : [])

  const columns: Column<Row>[] = [
    { key: 'company', head: 'Company', primary: true, cell: (r) => <><Link className="st-strong" to={`/staff/companies/${r.company_id}`}>{r.company}</Link>{r.blocked && r.state === 'queued' ? <div className="st-warn" data-testid="st-blocked">{r.blocked}</div> : null}{r.old_wording ? <div className="st-warn" data-testid="st-row-old-wording">Old wording</div> : null}{r.note ? <div className="st-warn" data-testid="st-row-note">{r.note}</div> : null}{r.greeting_note ? <div className="st-warn" data-testid="st-row-greeting">{r.greeting_note}</div> : null}{r.message ? null : <div className="st-muted" data-testid="st-link-only">Link only</div>}</> },
    { key: 'person', head: 'Person', label: 'Person', cell: (r) => (r.person_id ? <Link to={`/staff/people/${r.person_id}`}>{r.person}</Link> : r.person) },
    { key: 'warm', head: 'Warm', phone: false, cell: (r) => (r.warm.length ? <span data-testid="st-warm-cell">{r.warm.map(nameOf).join(', ')}</span> : routeWord(r.route) && r.route?.startsWith('warm') ? routeWord(r.route) : null) },
    { key: 'list', head: 'List', phone: false, nowrap: true, cell: (r) => r.list },
    { key: 'stage', head: 'Stage', phone: false, nowrap: true, cell: (r) => r.stage_label },
    { key: 'owner', head: 'Owner', phone: false, nowrap: true, cell: (r) => nameOf(r.owner) },
    { key: 'last', head: 'Last touch', phone: false, nowrap: true, cell: lastTouch },
    { key: 'state', head: '', label: 'Marked', nowrap: true, cell: (r) => (r.state === 'queued' ? null : <span className="st-muted" data-testid="st-send-state">{r.state === 'sent' ? 'Sent' : 'Skipped'}{r.done_by ? ` by ${nameOf(r.done_by)}` : ''}</span>) },
  ]

  const owner = get('owner')
  const next = row ? rows[rows.findIndex((r) => r.id === row.id) + 1]?.id : undefined
  return (
    <Page title="Send" testid="st-send" sub={<ReadLine at={d?.at} sync={d?.sync} what="Queue" pages={d ? d.pages_checked_at : undefined} imported={d?.imported} />}>
      <div className="st-swipe"><Strip items={(d?.groups ?? []).map((g) => ({ key: g.key, label: g.label, n: g.n }))} value={group} onChange={(k) => put({ group: k === 'ready' ? null : k })} testid="st-groups" /></div>
      <FilterBar
        on={['owner', 'mine', 'list', 'state', 'pattern', 'nopage', 'internal'].filter((k) => get(k)).length}
        more={(
          <>
            <input className="st-input" aria-label="Link pattern" placeholder="Link pattern" value={get('pattern')} data-testid="st-pattern" onChange={(e) => put({ pattern: e.target.value })} />
            <Check label="Link only" value={get('nopage') === '1'} testid="st-nopage" onChange={(v) => put({ nopage: v ? '1' : null })} />
            <Check label="Show internal" value={get('internal') === '1'} testid="st-internal" onChange={(v) => put({ internal: v ? '1' : null })} />
          </>
        )}
        search={<SearchField value={get('q')} onChange={(v) => put({ q: v })} placeholder="Search company, founder or slug" testid="st-q" />}
      >
        <Select label="Whose" value={get('mine') ? 'mine' : owner} testid="st-whose" onChange={(v) => put(v === 'mine' ? { mine: me, owner: null } : { owner: v, mine: null })} options={[{ value: '', label: 'Everyone' }, ...(me ? [{ value: 'mine', label: 'Mine and unowned' }] : []), ...(meta.data?.owners ?? []).map((o) => ({ value: o, label: nameOf(o) }))]} />
        <Select label="List" value={get('list')} testid="st-list" onChange={(v) => put({ list: v })} options={[{ value: '', label: 'Every list' }, ...(d?.lists ?? []).map((l) => ({ value: l.list, label: `${l.list} (${l.queued})` }))]} />
        <Select label="State" value={state} testid="st-state" onChange={(v) => put({ state: v === 'queued' ? null : v })} options={[{ value: 'queued', label: 'To do' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All' }]} />
        <Select label="Source tag" value={src} testid="st-src" onChange={(v) => { setSrc(v); keepSrc(v) }} options={SOURCES} />
      </FilterBar>
      {d?.hidden_yc ? <p className="st-note" data-testid="st-hidden-yc">{d.hidden_yc} YC {d.hidden_yc === 1 ? 'company' : 'companies'} left off, because this is a cold email list.</p> : null}
      {q.error ? <Err error={q.error} /> : !d ? <Busy /> : (
        <div className="st-send-wrap">
          <div className="st-send-list">
            {d.truncated ? <TooMany shown={rows.length} /> : null}
            <Table
              rows={rows} columns={columns} rowKey={(r) => r.id} cur={cur} onOpen={(r) => setCur(r.id)} testid="st-send-table"
              rowAttrs={(r) => ({ 'data-done': r.state === 'queued' ? undefined : r.state })}
              empty={state === 'queued' ? 'Nothing to send in this group.' : 'Nothing here.'}
            />
          </div>
          {row ? (
            <Panel
              onNext={next ? () => setCur(next) : undefined}
              onVariant={(n) => void copyVariant(row, n)}
              r={row} message={message(row)} link={link(row)} close={() => setCur(null)}
              onCopy={() => void copyMessage(row)} onLink={() => void copyLink(row)} onOpen={() => void openLinkedIn(row)} onPreview={() => preview(row)}
              onSent={() => void markSent(row)} onSkip={() => void skip(row)} onUndo={() => void undoRow(row)}
            />
          ) : <div className="st-send-panel st-send-idle"><p className="st-note">Press j to start.</p></div>}
        </div>
      )}
    </Page>
  )
}

function Panel({ onNext, onVariant, r, message, link, close, onCopy, onLink, onOpen, onPreview, onSent, onSkip, onUndo }: {
  onNext?: () => void; onVariant: (n: number) => void; r: Row; message: string | null; link: string | null; close: () => void
  onCopy: () => void; onLink: () => void; onOpen: () => void; onPreview: () => void; onSent: () => void; onSkip: () => void; onUndo: () => void
}) {
  const open = r.state === 'queued'
  return (
    <aside className="st-send-panel open" aria-label={`Send to ${r.company}`} data-testid="st-send-panel">
      <div className="st-send-head">
        <div>
          <Link className="st-strong" to={`/staff/companies/${r.company_id}`}>{r.company}</Link>
          <div className="st-muted">{[r.person, r.person_title, r.channel === 'linkedin' ? 'LinkedIn' : nameOf(r.channel)].filter(Boolean).join(', ')}</div>
        </div>
        <span className="st-row st-send-close">
          {onNext ? <Button tone="quiet" onClick={onNext} data-testid="st-send-next">Next</Button> : null}
          <Button tone="quiet" onClick={close} data-testid="st-send-close">Close</Button>
        </span>
      </div>
      {r.blocked && open && r.blocked !== r.already ? <div className="st-banner bad" data-testid="st-send-blocked">{r.blocked}. {r.refused ? "This can't go." : 'Sending needs a reason.'}</div> : null}
      {r.already && open ? <div className="st-banner" data-testid="st-send-messaged">{r.already}, {since(r.messaged_at)}</div> : null}
      {r.note ? <div className="st-banner" data-testid="st-send-note">{r.note}</div> : null}
      {r.greeting_note ? <div className="st-banner" data-testid="st-send-greeting">{r.greeting_note}</div> : null}
      {r.old_wording ? <div className="st-banner bad" data-testid="st-old-wording">Old wording. Read it before it goes</div> : null}
      {message ? <pre className="st-send-msg" data-testid="st-send-message">{message}</pre> : <p className="st-note">Link only. This row has no message.</p>}
      {link ? <p className="st-send-link" data-testid="st-send-link">{link}</p> : <p className="st-note">No link on this row.</p>}
      {!r.page_slug ? <p className="st-muted st-send-page">No page on record</p> : r.page_checked_at ? <p className="st-muted st-send-page">{pageCheck({ checked_at: r.page_checked_at, check_status: r.page_check_status, state: r.page_state ?? '' })}</p> : null}
      {r.variants.length > 1 ? (
        <div className="st-row" data-testid="st-send-variants">
          {r.variants.map((v, i) => <Button key={i} tone="quiet" title={v} onClick={() => onVariant(i)} data-testid={`st-send-variant-${i + 1}`}>Message {i + 1}</Button>)}
        </div>
      ) : null}
      <div className="st-row">
        {message ? <Button onClick={onCopy} keyHint="c" data-testid="st-send-copy">Copy message</Button> : null}
        <Button onClick={onLink} keyHint="l" data-testid="st-send-copy-link">Copy link</Button>
        <Button onClick={onOpen} keyHint="o" data-testid="st-send-linkedin">{r.linkedin_url ? 'LinkedIn' : 'Find on LinkedIn'}</Button>
        <Button tone="quiet" onClick={onPreview} keyHint="p" data-testid="st-send-preview">Preview</Button>
      </div>
      {open ? (
        <div className="st-row">
          {r.refused ? null : <Button tone={r.blocked ? 'danger' : 'primary'} onClick={onSent} keyHint="d" data-testid="st-send-sent">Mark sent</Button>}
          <Button tone="quiet" onClick={onSkip} keyHint="s" data-testid="st-send-skip">Skip</Button>
        </div>
      ) : (
        <div className="st-row">
          <span className="st-muted" data-testid="st-send-done">{r.state === 'sent' ? 'Sent' : 'Skipped'} by {nameOf(r.done_by)}, {since(r.done_at)}{r.override_reason ? `: ${r.override_reason}` : ''}</span>
          {r.undo_until ? <Undo until={r.undo_until} onUndo={onUndo} /> : null}
        </div>
      )}
    </aside>
  )
}

function Undo({ until, onUndo }: { until: string; onUndo: () => void }) {
  return new Date(until).getTime() > Date.now() ? <Button tone="quiet" onClick={onUndo} keyHint="u" data-testid="st-send-undo">Undo</Button> : null
}
